if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // BATCH 7 FIX #9: Event listener cleanup for memory leak prevention
        this.boundHandlers = {
          submit: this.onSubmitHandler.bind(this),
        };

        // FIX #9: Enhanced null/undefined checks for robustness
        this.form = this.querySelector('form');
        if (!this.form) {
          console.error('ProductForm: No form element found');
          return;
        }

        const variantIdInput = this.variantIdInput;
        if (!variantIdInput) {
          console.error('ProductForm: No variant ID input found');
          return;
        }

        variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.boundHandlers.submit);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const submitButton = this.querySelector('[type="submit"]');
        if (!submitButton) {
          console.error('ProductForm: No submit button found');
          return;
        }

        this.submitButton = submitButton;
        this.submitButtonText = submitButton.querySelector('span');
        if (!this.submitButtonText) {
          this.submitButtonText = submitButton;
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // BATCH 8 FIX #4: Setup form field error associations
        this.setupFormFieldAssociations();
      }

      setupFormFieldAssociations() {
        // BATCH 8 FIX #4: Link form fields to error messages
        const formFields = this.form.querySelectorAll('input[required], select[required], textarea[required]');
        formFields.forEach((field, index) => {
          if (!field.id) {
            field.id = `form-field-${index}-${Date.now()}`;
          }
          // Link field to error message using aria-describedby
          if (!field.getAttribute('aria-describedby')) {
            field.setAttribute('aria-describedby', `${field.id}-error`);
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // BATCH 6 FIX #9: Validate form before submission
        if (!this.validateForm()) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        // FIX #9: Enhanced error handling with null checks
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
          // Announce loading state to screen readers
          if (!spinner.getAttribute('aria-label')) {
            spinner.setAttribute('aria-label', 'Loading, please wait');
          }
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || 'An error occurred. Please try again.');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // Handle fetch error gracefully
            this.handleErrorMessage(e.message || 'Unable to add item to cart. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        // FIX #1: Ensure error message wrapper has proper role and aria attributes for screen readers
        if (!this.errorMessageWrapper.getAttribute('role')) {
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessageWrapper.setAttribute('aria-live', 'polite');
          this.errorMessageWrapper.setAttribute('aria-atomic', 'true');
        }

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          // Ensure focus moves to error message for accessibility
          if (typeof this.errorMessageWrapper.focus === 'function') {
            this.errorMessageWrapper.focus();
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // BATCH 6 FIX #9: Client-side form validation
      validateForm() {
        if (!this.form) return false;

        let isValid = true;
        const requiredFields = this.form.querySelectorAll('[required]');

        requiredFields.forEach((field) => {
          if (!field.value || field.value.trim() === '') {
            isValid = false;
            field.setAttribute('aria-invalid', 'true');

            // BATCH 8 FIX #4: Show error message associated with field
            const fieldId = field.id || `form-field-${Math.random()}`;
            const errorId = `${fieldId}-error`;
            const fieldName = field.getAttribute('aria-label') || field.getAttribute('name') || 'This field';
            const errorMsg = `${fieldName} is required.`;

            // Create or update error message element
            let errorElement = this.form.querySelector(`#${errorId}`);
            if (!errorElement) {
              errorElement = document.createElement('span');
              errorElement.id = errorId;
              errorElement.setAttribute('role', 'alert');
              errorElement.className = 'field-error-message';
              field.parentNode.insertBefore(errorElement, field.nextSibling);
            }
            errorElement.textContent = errorMsg;
            errorElement.style.display = 'block';

            this.handleErrorMessage(errorMsg);
          } else {
            field.setAttribute('aria-invalid', 'false');
            // Hide error message for valid field
            const errorId = `${field.id}-error`;
            const errorElement = this.form.querySelector(`#${errorId}`);
            if (errorElement) {
              errorElement.style.display = 'none';
            }
          }
        });

        return isValid;
      }

      // BATCH 7 FIX #9: Clean up event listeners on element removal
      disconnectedCallback() {
        if (this.form && this.boundHandlers.submit) {
          this.form.removeEventListener('submit', this.boundHandlers.submit);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
